import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { 
  Layers, 
  FileText, 
  TrendingUp,
  RefreshCw,
  Filter,
  ChevronRight,
  CheckCircle2,
  AlertCircle,
  Clock,
  DollarSign,
  BarChart3,
  PieChart
} from 'lucide-react';
import { listDocuments } from '../lib/api';
import { formatRelativeTime } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Document type styling
const DOC_TYPE_STYLES = {
  'EOB': { bg: 'bg-blue-50', text: 'text-blue-700', icon: FileText },
  'ERA': { bg: 'bg-purple-50', text: 'text-purple-700', icon: FileText },
  'Denial Letter': { bg: 'bg-red-50', text: 'text-red-700', icon: AlertCircle },
  'EOB - Denial': { bg: 'bg-red-50', text: 'text-red-700', icon: AlertCircle },
  'Recoupment Notice': { bg: 'bg-amber-50', text: 'text-amber-700', icon: DollarSign },
  'UNKNOWN': { bg: 'bg-gray-50', text: 'text-gray-700', icon: FileText }
};

// Claim category styling
const CATEGORY_STYLES = {
  'FULL_PAYMENT': { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Full Payment' },
  'PARTIAL_PAYMENT': { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Partial Payment' },
  'DENIAL': { bg: 'bg-red-100', text: 'text-red-700', label: 'Denial' },
  'ZERO_PAYMENT': { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Zero Payment' },
  'RECOUPMENT': { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Recoupment' },
  'ADJUSTMENT': { bg: 'bg-slate-100', text: 'text-slate-700', label: 'Adjustment' },
  'PENDING': { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Pending' }
};

// Queue styling
const QUEUE_STYLES = {
  'AUTO_POST': { bg: 'bg-emerald-50', text: 'text-emerald-700' },
  'REVIEW_HIGH': { bg: 'bg-amber-50', text: 'text-amber-700' },
  'REVIEW': { bg: 'bg-blue-50', text: 'text-blue-700' },
  'APPEALS': { bg: 'bg-purple-50', text: 'text-purple-700' },
  'MANUAL': { bg: 'bg-slate-50', text: 'text-slate-700' },
  'EXCEPTIONS': { bg: 'bg-red-50', text: 'text-red-700' }
};

function StatCard({ title, value, subtitle, icon: Icon, color = 'primary' }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardDescription className="text-xs">{title}</CardDescription>
          <Icon className={`w-4 h-4 text-${color}`} />
        </div>
        <CardTitle className="text-2xl">{value}</CardTitle>
      </CardHeader>
      {subtitle && (
        <CardContent>
          <span className="text-xs text-muted-foreground">{subtitle}</span>
        </CardContent>
      )}
    </Card>
  );
}

function DistributionChart({ data, title, colorMap }) {
  const items = Object.entries(data || {}).sort((a, b) => b[1] - a[1]);
  const total = items.reduce((sum, [_, count]) => sum + count, 0);

  if (items.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <PieChart className="w-8 h-8 mx-auto mb-2 opacity-30" />
        <p>No data available</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {items.map(([key, count]) => {
        const style = colorMap[key] || { bg: 'bg-gray-100', text: 'text-gray-700' };
        const percentage = total > 0 ? (count / total) * 100 : 0;
        const label = style.label || key.replace(/_/g, ' ');
        
        return (
          <div key={key} className="space-y-1">
            <div className="flex items-center justify-between text-sm">
              <span className={`px-2 py-0.5 rounded ${style.bg} ${style.text}`}>
                {label}
              </span>
              <span className="font-medium">{count} ({percentage.toFixed(1)}%)</span>
            </div>
            <Progress value={percentage} className="h-1.5" />
          </div>
        );
      })}
    </div>
  );
}

function QueueDistribution({ data }) {
  const items = Object.entries(data || {}).sort((a, b) => b[1] - a[1]);
  const total = items.reduce((sum, [_, count]) => sum + count, 0);

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
      {items.map(([queue, count]) => {
        const style = QUEUE_STYLES[queue] || { bg: 'bg-gray-50', text: 'text-gray-700' };
        const percentage = total > 0 ? (count / total) * 100 : 0;
        
        return (
          <div 
            key={queue} 
            className={`p-3 rounded-lg border ${style.bg}`}
          >
            <div className="flex items-center justify-between mb-1">
              <span className={`text-xs font-medium ${style.text}`}>
                {queue.replace(/_/g, ' ')}
              </span>
            </div>
            <div className="text-xl font-bold">{count}</div>
            <Progress value={percentage} className="h-1 mt-1" />
          </div>
        );
      })}
    </div>
  );
}

function ClassifiedDocumentsTable({ typeFilter, categoryFilter }) {
  const navigate = useNavigate();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['classifiedDocs', typeFilter, categoryFilter],
    queryFn: async () => {
      const params = { limit: 50, status: 'complete' };
      if (typeFilter && typeFilter !== ' ') params.classification = typeFilter;
      return listDocuments(params);
    }
  });

  const documents = data?.documents || [];

  // Filter by category if needed
  const filteredDocs = categoryFilter && categoryFilter !== ' '
    ? documents.filter(d => d.claim_category === categoryFilter)
    : documents;

  const getPriorityColor = (score) => {
    if (score >= 80) return 'text-red-600';
    if (score >= 60) return 'text-amber-600';
    return 'text-emerald-600';
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-16" />)}
      </div>
    );
  }

  if (filteredDocs.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <Layers className="w-12 h-12 mx-auto mb-3 opacity-30" />
        <p>No classified documents found</p>
      </div>
    );
  }

  return (
    <div className="border rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/50">
            <TableHead>Document</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Category</TableHead>
            <TableHead>Priority</TableHead>
            <TableHead>Queue</TableHead>
            <TableHead>Confidence</TableHead>
            <TableHead>Classified</TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredDocs.map((doc) => {
            const typeStyle = DOC_TYPE_STYLES[doc.classification_type] || DOC_TYPE_STYLES.UNKNOWN;
            const categoryStyle = CATEGORY_STYLES[doc.claim_category] || CATEGORY_STYLES.PENDING;
            const queueStyle = QUEUE_STYLES[doc.recommended_queue] || { bg: 'bg-gray-50', text: 'text-gray-700' };
            
            return (
              <TableRow 
                key={doc.id} 
                data-testid="classified-doc-row"
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => navigate(`/documents/${doc.id}`)}
              >
                <TableCell>
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-muted-foreground" />
                    <span className="font-medium text-sm truncate max-w-[200px]">
                      {doc.filename}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge className={`${typeStyle.bg} ${typeStyle.text}`}>
                    {doc.classification_type || 'Unknown'}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge className={`${categoryStyle.bg} ${categoryStyle.text}`}>
                    {categoryStyle.label}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <span className={`font-bold ${getPriorityColor(doc.priority_score || 0)}`}>
                      {doc.priority_score || 0}
                    </span>
                    <Progress 
                      value={doc.priority_score || 0} 
                      className="w-12 h-1.5" 
                    />
                  </div>
                </TableCell>
                <TableCell>
                  {doc.recommended_queue && (
                    <Badge variant="outline" className={`${queueStyle.bg} ${queueStyle.text}`}>
                      {doc.recommended_queue.replace(/_/g, ' ')}
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Progress 
                      value={(doc.classification_confidence || 0) * 100} 
                      className="w-12 h-1.5" 
                    />
                    <span className="text-xs font-mono">
                      {Math.round((doc.classification_confidence || 0) * 100)}%
                    </span>
                  </div>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {doc.classified_at ? formatRelativeTime(doc.classified_at) : '-'}
                </TableCell>
                <TableCell>
                  <Button size="sm" variant="ghost">
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

export default function ClassifyPage() {
  const [typeFilter, setTypeFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  // Fetch classification statistics
  const { data: stats, isLoading: statsLoading, refetch } = useQuery({
    queryKey: ['classifyStats'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/classify/stats`);
      if (!res.ok) throw new Error('Failed to fetch classification stats');
      return res.json();
    },
    refetchInterval: 30000
  });

  // Calculate totals
  const totalDocs = Object.values(stats?.by_document_type || {}).reduce((a, b) => a + b, 0);
  const autoPostCount = stats?.by_queue?.AUTO_POST || 0;
  const reviewCount = (stats?.by_queue?.REVIEW || 0) + (stats?.by_queue?.REVIEW_HIGH || 0);

  if (statsLoading) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          <Skeleton className="h-10 w-64" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-28" />)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6" data-testid="classify-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground font-heading">Classification</h1>
          <p className="text-muted-foreground mt-1">Document classification and routing analysis</p>
        </div>
        <div className="flex items-center gap-3 mt-4 sm:mt-0">
          <Button variant="outline" onClick={() => refetch()}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          title="Total Classified"
          value={totalDocs}
          subtitle="Documents processed"
          icon={Layers}
        />
        <StatCard
          title="Auto-Post Ready"
          value={autoPostCount}
          subtitle="Can be posted automatically"
          icon={CheckCircle2}
          color="emerald-500"
        />
        <StatCard
          title="Review Required"
          value={reviewCount}
          subtitle="Need manual review"
          icon={Clock}
          color="amber-500"
        />
        <StatCard
          title="Avg Priority"
          value={stats?.average_priority_score || 0}
          subtitle="Priority score (1-100)"
          icon={TrendingUp}
        />
      </div>

      {/* Analysis Panels */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Document Type Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Document Types
            </CardTitle>
            <CardDescription>Distribution by document type</CardDescription>
          </CardHeader>
          <CardContent>
            <DistributionChart 
              data={stats?.by_document_type} 
              colorMap={Object.fromEntries(
                Object.entries(DOC_TYPE_STYLES).map(([k, v]) => [k, { ...v, label: k }])
              )}
            />
          </CardContent>
        </Card>

        {/* Claim Category Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-primary" />
              Claim Categories
            </CardTitle>
            <CardDescription>Payment status breakdown</CardDescription>
          </CardHeader>
          <CardContent>
            <DistributionChart 
              data={stats?.by_claim_category} 
              colorMap={CATEGORY_STYLES}
            />
          </CardContent>
        </Card>
      </div>

      {/* Queue Distribution */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Layers className="w-5 h-5 text-primary" />
            Routing Distribution
          </CardTitle>
          <CardDescription>Documents by recommended queue</CardDescription>
        </CardHeader>
        <CardContent>
          <QueueDistribution data={stats?.by_queue} />
        </CardContent>
      </Card>

      {/* Documents Table */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                Classified Documents
              </CardTitle>
              <CardDescription>
                View classification details
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-[160px]">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value=" ">All Types</SelectItem>
                  <SelectItem value="EOB">EOB</SelectItem>
                  <SelectItem value="ERA">ERA</SelectItem>
                  <SelectItem value="EOB - Denial">Denial Letter</SelectItem>
                  <SelectItem value="Recoupment Notice">Recoupment Notice</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value=" ">All Categories</SelectItem>
                  <SelectItem value="FULL_PAYMENT">Full Payment</SelectItem>
                  <SelectItem value="PARTIAL_PAYMENT">Partial Payment</SelectItem>
                  <SelectItem value="DENIAL">Denial</SelectItem>
                  <SelectItem value="ZERO_PAYMENT">Zero Payment</SelectItem>
                  <SelectItem value="RECOUPMENT">Recoupment</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <ClassifiedDocumentsTable 
            typeFilter={typeFilter} 
            categoryFilter={categoryFilter} 
          />
        </CardContent>
      </Card>
    </div>
  );
}
